import { Button } from 'primereact/button';
import { SubmitHandler } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Message } from 'primereact/message';
import Auth from '../../services/auth';
import AuthSession from '../../services/auth-session';
import Yup from '../../utils/yup';
import { useForm } from '../../components/Form/hooks';
import makeForm from '../../components/Form';
import logo from '../../assets/aqila.png';
import { ApiGlobalService } from '../../services';
import InvalidCredentialsError from '../../services/errors/invalid-credentials';
import UnexpectedLoginResponseError from '../../services/errors/unexpected-login-response';
import MissingTokenError from '../../services/errors/missing-token';

type LoginFormData = {
  login: string;
  senha: string;
};

const Form = makeForm<LoginFormData>();

const VALIDATION_SCHEMA = Yup.object().shape({
  login: Yup.string().required(),
  senha: Yup.string().required(),
});

const DEFAULT_VALUES = {
  login: '',
  senha: '',
};

const LOGIN_REDIRECT_DEFAULT = '/';

const isHandledLoginError = (
  error: unknown,
): error is
  | InvalidCredentialsError
  | UnexpectedLoginResponseError
  | MissingTokenError =>
  error instanceof InvalidCredentialsError ||
  error instanceof UnexpectedLoginResponseError ||
  error instanceof MissingTokenError;

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isDiscoveringBaseUrl, setIsDiscoveringBaseUrl] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const redirectUrl = useMemo(
    () => searchParams.get('redirect') || LOGIN_REDIRECT_DEFAULT,
    [searchParams],
  );

  const formObject = useForm<LoginFormData>({
    validationSchema: VALIDATION_SCHEMA,
    reValidateMode: 'onChange',
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    const initializeApiHostUrl = async () => {
      try {
        setIsDiscoveringBaseUrl(true);
        await ApiGlobalService.inicializeApiHostUrl();
      } catch (error) {
        console.error(error);
        navigate('/404');
      } finally {
        setIsDiscoveringBaseUrl(false);
      }
    };

    initializeApiHostUrl();
  }, [navigate]);

  const onSubmit: SubmitHandler<LoginFormData> = async ({ login, senha }) => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      await Auth.login({ login, senha });
      navigate(redirectUrl, { replace: true });
    } catch (error) {
      const detail = isHandledLoginError(error)
        ? error.message
        : 'Ocorreu um erro, tente novamente.';

      setErrorMessage(detail);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (AuthSession.isAuthenticated()) {
    return <Navigate to={redirectUrl} replace />;
  }

  return (
    <div className="login-page flex justify-content-center align-items-center min-h-screen">
      <Form
        formObject={formObject}
        onSubmit={onSubmit}
        className="form-container w-full mx-4 sm:w-8 md:w-5 lg:w-3"
      >
        <div className="header h-2rem" />
        <div className="surface-card px-4 pt-4">
          <div className="flex justify-content-center w-full mb-4 ">
            <img src={logo} alt="Aqila" width={200} />
          </div>
          <Form.InputText
            name="login"
            placeholder="Usuário"
            className="w-full"
            addonLeft={<i className="pi pi-user" />}
          />
          <Form.InputText
            name="senha"
            placeholder="Senha"
            className="w-full"
            type="password"
            addonLeft={<i className="pi pi-lock" />}
          />
          {errorMessage && (
            <Message
              severity="error"
              className="mb-4 w-full"
              text={errorMessage}
            />
          )}
          <Button
            type="submit"
            label="ENTRAR"
            className="w-full button-secondary mb-4"
            loading={isLoading || isDiscoveringBaseUrl}
            disabled={isLoading || isDiscoveringBaseUrl}
          />
        </div>
        <div className="flex justify-content-center w-full bg-gray-500">
          <span className="text-center text-primary-900 text-xs m-1">
            © Copyright - Agro1
          </span>
        </div>
      </Form>
    </div>
  );
};

export default LoginPage;
